/**
 * ArcGIS export-based TileLayer.
 *
 * Wraps an ArcGIS MapServer service that is natively in a non-Web-Mercator
 * spatial reference (typical case in EU national portals). Instead of one
 * giant `export` request for the whole viewport (which times out for
 * country-scale views and breaks esri-leaflet's `dynamicMapLayer` with
 * `Cannot read properties of null` errors), we fire ONE `export` per
 * Leaflet tile.
 *
 * Each tile is 256x256 → upstream renders quickly → never times out → no
 * cascading null errors on layer remove.
 *
 * The server reprojects each tile on the fly because we ask for
 * `bboxSR=3857&imageSR=3857`, while the source data lives in 3794.
 */
import L from "leaflet";
import { createLayerComponent } from "@react-leaflet/core";

const ArcGISExportLayer = L.TileLayer.extend({
  options: {
    tileSize: 256,
    opacity: 1,
    minZoom: 0,
    maxZoom: 19,
    crossOrigin: false,
  },

  initialize(serviceUrl, options) {
    this._serviceUrl = serviceUrl.replace(/\/$/, "");
    L.setOptions(this, options || {});
  },

  // L.TileLayer's createTile uses {z}/{x}/{y} substitution which we don't
  // want. Override to build an export URL from the tile's geographic bbox.
  createTile(coords, done) {
    const tile = document.createElement("img");
    const size = this.getTileSize();

    L.DomEvent.on(tile, "load", () => done(null, tile));
    // Graceful fallback: a tile that fails to load is just dropped silently
    // (no console spam, no crashing parent layer). The map continues to work.
    L.DomEvent.on(tile, "error", () => {
      tile.style.visibility = "hidden";
      done(null, tile);
    });

    if (this.options.crossOrigin) {
      tile.crossOrigin = this.options.crossOrigin === true ? "" : this.options.crossOrigin;
    }
    tile.alt = "";
    tile.setAttribute("role", "presentation");
    tile.style.opacity = this.options.opacity;
    tile.src = this._tileUrl(coords, size);
    return tile;
  },

  _tileUrl(coords, size) {
    const tileBounds = this._tileCoordsToBounds(coords);
    // Project Leaflet's lat/lng bounds to EPSG:3857 (mercator meters), which
    // is the bbox CRS the ArcGIS server expects.
    const nw = L.CRS.EPSG3857.project(tileBounds.getNorthWest());
    const se = L.CRS.EPSG3857.project(tileBounds.getSouthEast());
    const bbox = [nw.x, se.y, se.x, nw.y].join(",");
    const params = new URLSearchParams({
      bbox,
      bboxSR: "3857",
      imageSR: "3857",
      size: `${size.x},${size.y}`,
      format: "png32",
      transparent: "true",
      f: "image",
    });
    return `${this._serviceUrl}/export?${params.toString()}`;
  },
});

const arcgisExportLayer = (url, options) => new ArcGISExportLayer(url, options);

// React-Leaflet wrapper using the modern createLayerComponent API.
const ArcGISExportLayerReact = createLayerComponent(
  (props, ctx) => {
    const { url, ...options } = props;
    const layer = arcgisExportLayer(url, options);
    return { instance: layer, context: { ...ctx, overlayContainer: layer } };
  },
  (instance, props, prevProps) => {
    if (props.opacity !== prevProps.opacity) {
      instance.setOpacity(props.opacity);
    }
  }
);

export default ArcGISExportLayerReact;
